import { type FC, useEffect, useState } from 'react';
import { useToast, TextLink, Text } from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import { useQueryParam, StringParam } from 'use-query-params';

import type { IStore } from '../../stores/Store';
import { CandidateManagementApp } from '../../App/AppPageBlock';
import { navigateToJobListPage } from './utils/utils';
import fetchInitialJobApplications from './fetchInitialJobApplications';
import useQueryParamsStore from '../../utils/hooks/useQueryParamsStore';

import { handleAdvertiserPositionAccess } from './utils/handleAdvertiserAccessPositionCheck';
import fetchAccessAndJobInformationByJobId from 'src/graphql/fetchAccessAndJobInformationByJobId';
import fetchAccessAndJobInformationByPositionId from 'src/graphql/fetchAccessAndJobInformationByPositionId';
import { logError } from 'src/utils/logError';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from './.vocab';
import { getAdvertiserIds, isValidId } from 'src/utils/advertiserIds';
import { useStores } from 'src/utils/hooks/useStores';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from './queryParams';
import authServiceFactory from 'src/services/authServiceFactory';
import { ErrorContent } from 'src/App/ActionsPage/ErrorScreen/ErrorContent';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { CandidateManagementAppSkeleton } from 'src/App/AppPageBlock/CandidateManagementAppSkeleton';

type TranslateFn = ReturnType<typeof useTranslations>['t'];

export interface HandleEasyAccessTranslations {
  'application has been moved': ({
    firstName,
    bucket,
  }: {
    firstName: string;
    bucket: string;
  }) => string;
  close: () => string;
}

const handleEasyAccessMessages = (
  t: TranslateFn,
): HandleEasyAccessTranslations => ({
  'application has been moved': ({ firstName, bucket }) =>
    t('application has been moved', { firstName, bucket }) as string,
  close: () => t('close') as string,
});

interface Props {
  employerHeader: JSX.Element;
}
export type JobIdType = 'jobId' | 'positionId';

interface AccessAndGetJobProps {
  store: IStore;
  jobIdType: JobIdType;
  expectedJobId: number; // either jobId or PositionId
  expectedAdvertiserId: string; // either seekAdvertiserId or adcentreAdvertiserId
  selectedProspectId?: string;
}

export const checkAccessAndFetchJobInformation = async ({
  store,
  jobIdType,
  expectedJobId,
  expectedAdvertiserId,
}: AccessAndGetJobProps) => {
  const { advertiserAccess, job } =
    jobIdType === 'positionId'
      ? await fetchAccessAndJobInformationByPositionId(
          expectedJobId,
          expectedAdvertiserId,
        )
      : await fetchAccessAndJobInformationByJobId(
          expectedJobId,
          expectedAdvertiserId,
        );

  handleAdvertiserPositionAccess(store, expectedJobId, advertiserAccess);

  if (!job) {
    store.jobStore.setIsError();
    return false;
  }

  if (job.jobInformation.jobId == null || job.jobInformation.jobId <= 0) {
    logError(
      {
        errorCode: 'INVALID_JOB_ID',
        description:
          'Invalid JobId upon return from checkAccessAndFetchJobInformation',
      },
      {
        positionId: job.jobInformation.positionId,
        jobId: job.jobInformation.jobId,
      },
    );
    store.jobStore.setIsError();
    return false;
  }

  store.jobStore.setJob(job);

  return store.jobStore.hasAccess && !store.jobStore.isError;
};

const CandidateManagementPage: FC<Props> = ({ employerHeader }) => {
  const store = useStores();
  const [query, setQuery] = useQueryParamsStore();
  const showToast = useToast();
  const { getLabelFromBucket } = useBucketLabel();
  const { t } = useTranslations(translations);
  const messages = handleEasyAccessMessages(t);
  const [hasLoginRedirectError, setHasLoginRedirectError] = useState(false);
  const { language } = useAppConfig();
  const [loadedOnce, setLoadedOnce] = useState(false);

  const [tab, setTabQueryParam] = useQueryParam(
    CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
    StringParam,
  );

  useEffect(() => {
    if (!store.loadInitialised) {
      const { advertiserId, seekAdvertiserId } = getAdvertiserIds();

      if (!isValidId(advertiserId) || !isValidId(seekAdvertiserId)) {
        const authService = authServiceFactory();

        authService
          .loginWithRedirect()
          .then(() => {})
          .catch((error) => {
            logError(
              {
                errorCode: 'LOGIN_WITH_REDIRECTION_URL',
                description: 'Error attempting: loginWithRedirect',
              },
              {
                error,
              },
            );
            setHasLoginRedirectError(true);
          });
        return;
      }

      const {
        positionId: queryPositionId,
        jobId: queryJobId,
        selectedProspectId,
      } = query;
      let accessAndGetJobProps: AccessAndGetJobProps;

      if (queryPositionId) {
        accessAndGetJobProps = {
          store,
          jobIdType: 'positionId',
          expectedAdvertiserId: advertiserId,
          expectedJobId: queryPositionId,
          selectedProspectId,
        };
      } else if (queryJobId) {
        accessAndGetJobProps = {
          store,
          jobIdType: 'jobId',
          expectedAdvertiserId: seekAdvertiserId,
          expectedJobId: queryJobId,
          selectedProspectId,
        };
      } else {
        return navigateToJobListPage();
      }

      checkAccessAndFetchJobInformation(accessAndGetJobProps)
        .then((success) => {
          if (success) {
            const positionId = store.jobStore.job.jobInformation.positionId;
            const jobId = store.jobStore.job.jobInformation.jobId;

            fetchInitialJobApplications(
              store,
              (page: number | undefined) => setQuery.updatePage(page),
              advertiserId!,
              seekAdvertiserId!,
              positionId,
              jobId,
              query,
              showToast,
              getLabelFromBucket,
              messages,
              language,
            ).then(() => {
              setLoadedOnce(true);
            });
          } else {
            setLoadedOnce(true);
          }
        })
        .catch((error) => {
          logError(
            {
              errorCode: 'FETCH_ACCESS_AND_JOB_INFORMATION',
              description: 'Error attempting: fetchInitialJobApplications',
            },
            {
              error,
            },
          );
          store.jobStore.setIsError(true);
          setLoadedOnce(true);
        });
    }
  }, [
    query.positionId,
    query.bucket,
    query.page,
    query.selectedProspectId,
    store,
    query.search,
    query,
    setQuery,
    showToast,
    getLabelFromBucket,
    messages,
    setTabQueryParam,
    tab,
    language,
  ]);

  useEffect(() => {
    if (query.referrer === 'edit-page-success') {
      showToast({
        message: t('Your job has been updated'),
        tone: 'positive',
        closeLabel: t('close'),
      });
      setQuery.clearReferrer();
    }
  }, [showToast, query.referrer, setQuery, t]);

  if (hasLoginRedirectError) {
    return (
      <ErrorContent
        errorCode="500"
        heading={t('Can we try that again?')}
        body={
          <Text tone="secondary">
            {t("Retry the page to see your candidate's profile", {
              Link: (children) => (
                <TextLink href={window.location.href}>{children}</TextLink>
              ),
            })}
          </Text>
        }
      />
    );
  }

  if (!loadedOnce) {
    return <CandidateManagementAppSkeleton />;
  }

  return <CandidateManagementApp employerHeader={employerHeader} />;
};

export default CandidateManagementPage;
